import { useEffect, useRef } from "react";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";

const EmailViewer: FC<Props> = ({ sourceContent }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const lastHeightRef = useRef(0);

  // Wstrzykuje do <head> iframe reset z !important. Robimy to stylesheetem, a nie
  // stylem inline na html/body, bo:
  //  - działa też w quirks mode (maile bez <!DOCTYPE html>), gdzie scrollerem jest <body>,
  //  - !important nie da się nadpisać stylami z treści maila,
  //  - gwarantuje brak paska przewijania niezależnie od zaokrągleń sub-pikselowych.
  const ensureResetStyle = (doc: Document) => {
    if (doc.getElementById("ev-reset-style")) return;
    const style = doc.createElement("style");
    style.id = "ev-reset-style";
    style.textContent =
      "html,body{margin:0!important;padding:0!important;overflow:hidden!important;}";
    (doc.head || doc.documentElement).appendChild(style);
  };

  const resizeIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const iframeContent =
      iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeContent || !iframeContent.body) return;

    const doc = iframeContent.documentElement;
    const body = iframeContent.body;

    body.style.fontFamily = "Rubik";
    body.style.fontSize = "14px";
    ensureResetStyle(iframeContent);

    // scrollHeight zwraca zaokrągloną w dół liczbę całkowitą – przy ułamkowej
    // wysokości linii (font Rubik) treść bywa o <1px wyższa, stąd Math.ceil + 1px zapasu.
    const height =
      Math.ceil(
        Math.max(
          body.scrollHeight,
          body.offsetHeight,
          doc.scrollHeight,
          doc.offsetHeight,
          body.getBoundingClientRect().height,
          doc.getBoundingClientRect().height,
        ),
      ) + 1;

    // ResizeObserver odpala się też po naszej własnej zmianie wysokości. Bez progu
    // wpadamy w pętlę sprzężenia (pasek pojawia się/znika → tekst się przełamuje →
    // zmiana wysokości → …), co dla maila będącego samym tekstem zostawiało stały
    // scroll. Zapisujemy ostatnią wartość i piszemy tylko przy realnej zmianie.
    if (Math.abs(height - lastHeightRef.current) <= 1) return;

    lastHeightRef.current = height;
    iframe.style.height = height + "px";
  };

  const handleLoad = () => {
    // nowy dokument w iframe – kasujemy zapamiętaną wysokość, by próg nie zablokował
    // pierwszego dopasowania po podmianie treści maila
    lastHeightRef.current = 0;
    resizeIframe();

    const iframe = iframeRef.current;
    const iframeContent =
      iframe?.contentDocument || iframe?.contentWindow?.document;

    if (!iframeContent) return;

    // Rubik to font webowy – ładuje się asynchronicznie i po podmianie tekst
    // reflowuje się o kilka px, dlatego przeliczamy wysokość ponownie po jego załadowaniu.
    // Uwaga: @font-face Rubika nie jest zadeklarowany w dokumencie iframe, więc samo
    // fonts.ready może nie czekać na jego swap – jawnie wymuszamy load() w kontekście
    // iframe (to rejestruje font jako oczekujący) i mierzymy dopiero po jego załadowaniu.
    iframeContent.fonts?.load('14px "Rubik"').then(resizeIframe).catch(() => {});
    iframeContent.fonts?.ready?.then(resizeIframe).catch(() => {});

    // obrazy doładowują się już po onLoad – każdy zwiększa wysokość treści
    iframeContent.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", resizeIframe);
        img.addEventListener("error", resizeIframe);
      }
    });

    // łapiemy pozostałe późne reflowy (web-fonty, dynamiczny layout)
    if (iframeContent.body && "ResizeObserver" in window) {
      resizeObserverRef.current?.disconnect();
      const ro = new ResizeObserver(() => resizeIframe());
      ro.observe(iframeContent.body);
      resizeObserverRef.current = ro;
    }
  };

  useEffect(() => {
    return () => resizeObserverRef.current?.disconnect();
  }, []);

  return (
    <iframe
      ref={iframeRef}
      className={styles.iframe}
      srcDoc={sourceContent}
      onLoad={handleLoad}
      scrolling="no"
      frameBorder="0"
      // bez allow-scripts → skrypty w treści maila się nie wykonują (anty-XSS);
      // allow-same-origin do auto-resize, allow-popups do klikania linków
      sandbox="allow-same-origin allow-popups"
    ></iframe>
  );
};

export default EmailViewer;
